import React, { useMemo, useState } from 'react';
import { ArrowDownRight, ArrowUpRight, ExternalLink } from 'lucide-react';
import data from '../../data/policy-makers-DataRequirements.json';
import { loadPolicyMakerProfile } from '../../utils/policyMakerProfileStorage';
import { ClickInfoTooltip } from '../ClickInfoTooltip';

const screenByRoute = (route) => data.screens.find((s) => s.route === route);
const getCollection = (route, name) => {
  const screen = screenByRoute(route);
  return screen?.data_collections.find((c) => c.name === name);
};
const sample = (route, name) => {
  const raw = getCollection(route, name)?.sample_data;
  return Array.isArray(raw) ? raw : [];
};
const sampleObject = (route, name) => {
  const raw = getCollection(route, name)?.sample_data;
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : null;
};

const SEV_DOT = { CRITICAL: 'bg-red-500', WARNING: 'bg-amber-500' };
const SEV_LABEL = { CRITICAL: 'Critical', WARNING: 'Warning' };

const ROUTE_BAND_DOT = {
  critical: 'bg-red-500',
  elevated: 'bg-amber-500',
  clear: 'bg-emerald-500',
  normal: 'bg-emerald-500',
};

const PLI_TONE_BG = {
  on_track: 'border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/50 dark:bg-emerald-950/20',
  behind: 'border-amber-200 dark:border-amber-900/60 bg-amber-50/50 dark:bg-amber-950/20',
  severe: 'border-red-200 dark:border-red-900/60 bg-red-50/50 dark:bg-red-950/20',
  mixed: 'border-blue-200 dark:border-blue-900/60 bg-blue-50/50 dark:bg-blue-950/20',
  below_target: 'border-amber-200 dark:border-amber-900/60 bg-amber-50/50 dark:bg-amber-950/20',
};

const ALLY_TONE = {
  yes: 'border-emerald-300 dark:border-emerald-800 bg-emerald-50/80 dark:bg-emerald-950/30',
  partly: 'border-amber-300 dark:border-amber-800 bg-amber-50/80 dark:bg-amber-950/30',
  no: 'border-red-300 dark:border-red-900/50 bg-red-50/50 dark:bg-red-950/20',
};

const DELIB_PILL = {
  yes: 'bg-rose-100 text-rose-900 dark:bg-rose-950/60 dark:text-rose-200 border-rose-200 dark:border-rose-900/50',
  partly: 'bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200 border-amber-200 dark:border-amber-900/50',
  no: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200 border-emerald-200 dark:border-emerald-900/50',
};

const RISK_PILL = {
  critical: 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200 border-red-200 dark:border-red-900/50',
  high: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200 border-amber-200 dark:border-amber-900/50',
  medium: 'bg-gray-50 text-gray-700 dark:bg-gray-800/60 dark:text-gray-300 border-gray-200 dark:border-gray-700',
};

const GP_TAG_KIND = {
  risk: 'border-rose-200 dark:border-rose-900/50 text-rose-800 dark:text-rose-200 bg-rose-50 dark:bg-rose-950/30',
  opportunity: 'border-emerald-200 dark:border-emerald-900/50 text-emerald-800 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-950/30',
  neutral: 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300',
};

const fmtPct = (v, signed = false) => {
  if (!Number.isFinite(v)) return '–';
  const s = signed && v > 0 ? '+' : '';
  return `${s}${v}%`;
};

const Card = ({ children, className = '' }) => (
  <div className={`bg-white dark:bg-gray-900/40 rounded-xl border border-gray-200 dark:border-gray-800 ${className}`}>{children}</div>
);
const Tag = ({ children, dot, className = '' }) => (
  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/30 whitespace-nowrap ${className}`}>
    {dot ? <span className={`h-1.5 w-1.5 rounded-full ${dot}`} /> : null}
    {children}
  </span>
);
const HeaderWithInfo = ({ label, meaning, useful }) => (
  <span className="inline-flex items-center gap-1">
    <span>{label}</span>
    <ClickInfoTooltip label={`About ${label}`}>
      <>
        <div className="font-semibold mb-0.5">What it means</div>
        <div className="mb-1.5">{meaning}</div>
        <div className="font-semibold mb-0.5">Why it is useful</div>
        <div>{useful}</div>
      </>
    </ClickInfoTooltip>
  </span>
);
const MutedLabel = ({ children, className = '' }) => (
  <div className={`text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-500 ${className}`}>{children}</div>
);
const SectionHeader = ({ screen }) => (
  (() => {
    const key = screen?.route || screen?.id;
    const HELP = {
      alerts: {
        meaning:
          'Curated supply-chain events flagged as needing attention this week.',
        useful:
          'Helps you prioritize actions by severity and quickly understand the policy-relevant angle.',
      },
      'import-exposure': {
        meaning:
          'A view of import exposure and where vulnerability concentrates across minerals and supply chains.',
        useful:
          'Supports mitigation planning by showing which exposures are most likely to turn into disruptions.',
      },
      'trade-agreements': {
        meaning:
          'A structured lens on trade agreements, dependencies, and “unused” provisions that can be activated.',
        useful:
          'Gives you a concrete checklist for converting frameworks into operational leverage.',
      },
      'domestic-capability': {
        meaning:
          'An assessment of domestic production/coverage against gaps in required minerals and programs.',
        useful:
          'Helps identify where domestic capacity must be accelerated to reduce dependency risk.',
      },
      'ports-logistics': {
        meaning:
          'Tracks port and logistics performance to surface delays before they become supply-chain impact.',
        useful:
          'Lets you coordinate ports, shipping lines, and supporting ministries to reduce dwell-time losses.',
      },
      'geopolitical-risk': {
        meaning:
          'Signals about geopolitical pressure points, partner risk, and resulting supply-chain sensitivity.',
        useful:
          'Helps you time engagement and prepare contingencies for partners and corridors under stress.',
      },
      scenarios: {
        meaning:
          'Scenario-based planning that translates signals into plausible next steps and recommended responses.',
        useful:
          'Gives decision-ready options so you can act early instead of reacting after disruption.',
      },
    };

    const help = HELP[key] || {
      meaning: 'This section summarizes a decision-relevant slice of supply-chain intelligence.',
      useful: 'Use it to quickly find what needs attention and what action path fits the situation.',
    };

    return (
      <div className="mb-6 pb-4 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-start gap-2">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">{screen?.title}</h2>
          <ClickInfoTooltip label={screen?.title ? `About ${screen.title}` : 'About this section'}>
            <>
              <div className="font-semibold mb-0.5">What it means</div>
              <div className="mb-1.5">{help.meaning}</div>
              <div className="font-semibold mb-0.5">Why it is useful</div>
              <div>{help.useful}</div>
            </>
          </ClickInfoTooltip>
        </div>
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {[screen?.persona, screen?.cadence].filter(Boolean).join(' · ')}
        </div>
      </div>
    );
  })()
);
const Delta = ({ value }) => {
  if (!Number.isFinite(value)) return null;
  const up = value >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  const cls = up ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400';
  return (
    <span className={`inline-flex items-center gap-0.5 text-sm font-medium tabular-nums ${cls}`}>
      <Icon className="h-3.5 w-3.5" />
      {fmtPct(Math.abs(value), false)}
    </span>
  );
};

function deliberatelyPill(dm) {
  const k = String(dm || '').toLowerCase();
  if (k === 'yes') return { label: 'Yes', cls: DELIB_PILL.yes };
  if (k === 'partly') return { label: 'Partly', cls: DELIB_PILL.partly };
  return { label: 'No', cls: DELIB_PILL.no };
}

const Hero = ({ profile }) => {
  const { meta } = data;
  const p = profile ?? loadPolicyMakerProfile();
  const name = p?.ministryDepartmentName?.trim() || meta.customer;
  const metaLine = p?.policyRole
    ? `${meta.context_notes.organization} · ${p.policyRole}`
    : `${meta.context_notes.organization} · ${meta.context_notes.division}`;

  return (
    <Card className="p-7 md:p-8">
      <h1 className="text-3xl md:text-[34px] font-semibold text-gray-900 dark:text-white tracking-tight">{name}</h1>
      <p className="text-gray-600 dark:text-gray-400 mt-2 text-[15px]">{metaLine}</p>
    </Card>
  );
};

const KpiStrip = () => {
  const k = data.meta.dashboard_kpis;
  const items = [
    { label: 'Critical alerts', value: k.critical_alerts, dot: SEV_DOT.CRITICAL },
    { label: 'Warning alerts', value: k.warning_alerts, dot: SEV_DOT.WARNING },
    { label: 'State action events', value: k.state_action_events },
    { label: 'Market events', value: k.market_events },
    { label: 'At-risk import value', value: k.at_risk_import_value_label },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {items.map((it) => (
        <Card key={it.label} className="p-4">
          <div className="flex items-center gap-1.5">
            {it.dot ? <span className={`h-1.5 w-1.5 rounded-full ${it.dot}`} /> : null}
            <MutedLabel>{it.label}</MutedLabel>
          </div>
          <div className="text-2xl font-semibold text-gray-900 dark:text-white mt-2 tabular-nums">{it.value}</div>
        </Card>
      ))}
    </div>
  );
};

const AlertsSection = () => {
  const screen = screenByRoute('alerts');
  const filtersObj = sampleObject('alerts', 'alert_feed_filters');
  const tabDefs = filtersObj?.tabs || [];
  const alerts = sample('alerts', 'live_alert_feed');
  const [activeTab, setActiveTab] = useState(tabDefs[0]?.id || 'all');

  const filtered = useMemo(() => {
    if (activeTab === 'all') return alerts;
    if (activeTab === 'critical') return alerts.filter((a) => a.severity === 'CRITICAL');
    if (activeTab === 'warning') return alerts.filter((a) => a.severity === 'WARNING');
    if (activeTab === 'state_action') return alerts.filter((a) => a.event_type === 'state_action');
    if (activeTab === 'market') return alerts.filter((a) => a.event_type === 'market');
    return alerts;
  }, [activeTab, alerts]);

  const footerLabel = `Showing ${filtered.length} of ${alerts.length}`;

  return (
    <section id="alerts" className="scroll-mt-24">
      <SectionHeader screen={screen} />
      <Card>
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-1 flex-wrap">
            {tabDefs.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setActiveTab(f.id)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                  activeTab === f.id ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                {f.id === 'critical' ? <span className={`h-1.5 w-1.5 rounded-full ${SEV_DOT.CRITICAL}`} /> : null}
                {f.id === 'warning' ? <span className={`h-1.5 w-1.5 rounded-full ${SEV_DOT.WARNING}`} /> : null}
                {f.id === 'state_action' ? <span className="h-1.5 w-1.5 rounded-full bg-red-600" /> : null}
                {f.id === 'market' ? <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> : null}
                {f.label}
                <span className="text-[10px] tabular-nums opacity-70">{f.count}</span>
              </button>
            ))}
          </div>
          <span className="text-xs text-gray-500">{footerLabel}</span>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-800">
          {filtered.map((a) => (
            <div key={a.alert_id} className="px-5 py-4">
              <div className="flex items-start gap-3">
                <span className={`mt-2 h-2 w-2 rounded-full flex-shrink-0 ${SEV_DOT[a.severity] || SEV_DOT.WARNING}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-1.5 text-[11px] text-gray-500">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-700 dark:text-gray-300">{SEV_LABEL[a.severity] || a.severity}</span>
                      <span>·</span>
                      <span>{a.category}</span>
                      <span>·</span>
                      <span>{a.timestamp_ago}</span>
                      {a.event_type === 'state_action' ? (
                        <Tag className="!border-red-300 dark:!border-red-800 !text-red-800 dark:!text-red-300 !bg-red-50 dark:!bg-red-950/30">
                          State action
                        </Tag>
                      ) : null}
                      {a.event_type === 'market' ? (
                        <Tag className="!border-emerald-300 dark:!border-emerald-800 !text-emerald-800 dark:!text-emerald-300 !bg-emerald-50 dark:!bg-emerald-950/30">
                          Market
                        </Tag>
                      ) : null}
                    </div>
                    <span className="inline-flex items-center gap-1 shrink-0">
                      <ExternalLink className="h-3 w-3" aria-hidden /> {a.source_name}
                    </span>
                  </div>
                  <h4 className="font-medium text-gray-900 dark:text-white text-[15px]">{a.title}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{a.summary}</p>
                  {a.impact ? (
                    <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                      <span className="text-gray-500 dark:text-gray-400">Supply chain impact · </span>
                      {a.impact}
                    </p>
                  ) : null}
                  {a.why_now ? (
                    <div className="mt-3">
                      <h5 className="text-sm font-semibold text-gray-900 dark:text-white">Why now</h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">{a.why_now}</p>
                    </div>
                  ) : null}
                  {a.tags?.length ? (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {a.tags.map((t) => (
                        <Tag key={t}>{t}</Tag>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </section>
  );
};

const ImportExposureSection = () => {
  const route = 'import-exposure';
  const screen = screenByRoute(route);
  const cards = sample(route, 'strategic_category_cards');
  const matrix = sample(route, 'import_vulnerability_matrix');
  const matters = sampleObject(route, 'why_deliberately_maintained_matters');
  const chinaBars = sample(route, 'china_dependence_by_sector');
  const pressure = sampleObject(route, 'import_bill_pressure_quarter');

  return (
    <section className="scroll-mt-24 space-y-6">
      <SectionHeader screen={screen} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map((c, i) => (
          <Card key={`${c.category}-${i}`} className="p-5">
            <MutedLabel>{c.category}</MutedLabel>
            <div className="mt-2 flex items-baseline gap-2 flex-wrap">
              <span className="text-2xl font-semibold tabular-nums">{c.annual_import_label}</span>
              <Delta value={typeof c.yoy_change_pct === 'number' ? c.yoy_change_pct : NaN} />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{c.footnote}</p>
          </Card>
        ))}
      </div>
      <Card className="p-5 overflow-x-auto">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Import exposure table</h3>
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="text-left text-[11px] uppercase text-gray-500 border-b border-gray-200 dark:border-gray-800">
              <th className="pb-2 pr-3">
                <HeaderWithInfo
                  label="Sector"
                  meaning="Strategic import category being assessed."
                  useful="Lets you compare vulnerability across sectors at a glance."
                />
              </th>
              <th className="pb-2 pr-3">
                <HeaderWithInfo
                  label="Annual import"
                  meaning="Approximate annual import value for that sector."
                  useful="Helps size the financial exposure if supply is disrupted."
                />
              </th>
              <th className="pb-2 pr-3">
                <HeaderWithInfo
                  label="Top source"
                  meaning="Main country or country pair supplying this sector."
                  useful="Shows where external dependency is concentrated."
                />
              </th>
              <th className="pb-2 pr-3">
                <HeaderWithInfo
                  label="Concentration"
                  meaning="Share of imports tied to the top source."
                  useful="Higher concentration implies higher disruption risk."
                />
              </th>
              <th className="pb-2 pr-3">
                <HeaderWithInfo
                  label="Domestic substitution"
                  meaning="How much domestic supply can replace imports in the near term."
                  useful="Indicates resilience and short-term recovery options."
                />
              </th>
              <th className="pb-2 pr-3">
                <HeaderWithInfo
                  label="Strategic reserve"
                  meaning="Buffer stock coverage available for that category."
                  useful="Shows how long India can absorb a supply shock."
                />
              </th>
              <th className="pb-2 pr-3">
                <HeaderWithInfo
                  label="Policy intent"
                  meaning="Whether dependency is deliberately maintained (yes/partly/no)."
                  useful="Separates market risk from strategic state-led leverage."
                />
              </th>
              <th className="pb-2">
                <HeaderWithInfo
                  label="Risk"
                  meaning="Overall risk tier for that sector."
                  useful="Supports quick prioritization for action and escalation."
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {matrix.map((row, i) => {
              const pill = deliberatelyPill(row.deliberately_maintained);
              return (
                <tr key={`${row.sector}-${i}`} className="border-b border-gray-100 dark:border-gray-800 align-top">
                  <td className="py-3 font-medium">{row.sector}</td>
                  <td className="py-3 tabular-nums whitespace-nowrap">{row.annual_import}</td>
                  <td className="py-3 whitespace-nowrap">
                    {row.top_source_flag ? <span className="mr-1">{row.top_source_flag}</span> : null}
                    {row.top_source}
                  </td>
                  <td className="py-3 tabular-nums">{row.concentration_pct}%</td>
                  <td className="py-3">{row.domestic_substitution}</td>
                  <td className="py-3">{row.strategic_reserve}</td>
                  <td className="py-3 max-w-xs">
                    <span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium border ${pill.cls}`}>{pill.label}</span>
                    {row.deliberately_maintained_note ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{row.deliberately_maintained_note}</p>
                    ) : null}
                  </td>
                  <td className="py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium border capitalize ${RISK_PILL[row.risk] || RISK_PILL.medium}`}>
                      {row.risk}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
      {matters?.body ? (
        <Card className="p-5 border-blue-200 dark:border-blue-900/40 bg-blue-50/60 dark:bg-blue-950/20">
          <h3 className="font-semibold text-gray-900 dark:text-white">{matters.title}</h3>
          <p className="text-sm text-gray-700 dark:text-gray-300 mt-3 leading-relaxed">{matters.body}</p>
        </Card>
      ) : null}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">China dependence by sector</h3>
          <div className="space-y-4">
            {chinaBars.map((row, i) => (
              <div key={`${row.sector}-${i}`}>
                <div className="flex justify-between text-sm mb-1 gap-2">
                  <span className="text-gray-800 dark:text-gray-200">{row.sector}</span>
                  <span className="tabular-nums font-medium">
                    {row.china_share_pct}%
                    {row.deliberately_maintained_tag ? (
                      <span className="ml-2 text-[11px] font-normal text-gray-500">{row.deliberately_maintained_tag}</span>
                    ) : null}
                  </span>
                </div>
                <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gray-900 dark:bg-gray-300"
                    style={{ width: `${Math.min(100, row.china_share_pct)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-5">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Import bill pressure this quarter</h3>
          <p className="text-xs text-gray-500 mb-4">Price and policy drivers adding to the INR import bill</p>
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {(pressure?.line_items || []).map((li, idx) => (
              <div key={`${li.category}-${idx}`} className="py-3 flex flex-col sm:flex-row sm:justify-between gap-2">
                <div>
                  <p className="font-medium text-sm">{li.category}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{li.context}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm tabular-nums font-semibold">{li.rupee_pressure_label}</p>
                  <p className="text-xs text-amber-600 dark:text-amber-400">{fmtPct(li.price_pct, false)} YoY headline</p>
                </div>
              </div>
            ))}
          </div>
          {pressure?.total_additional_pressure_label ? (
            <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-800 flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Total additional pressure</span>
              <span className="font-bold tabular-nums">{pressure.total_additional_pressure_label}</span>
            </div>
          ) : null}
        </Card>
      </div>
    </section>
  );
};

const TradeAgreementsSection = () => {
  const route = 'trade-agreements';
  const screen = screenByRoute(route);
  const rows = sample(route, 'trade_agreements_table');
  const gaps = sample(route, 'strategic_supply_chain_gaps');

  return (
    <section className="scroll-mt-24 space-y-6">
      <SectionHeader screen={screen} />
      <Card className="p-5 overflow-x-auto">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Trade agreements</h3>
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="text-left text-[11px] uppercase text-gray-500 border-b border-gray-200 dark:border-gray-800">
              <th className="pb-2 pr-3">Partner</th>
              <th className="pb-2 pr-3">Agreement</th>
              <th className="pb-2 pr-3">Stage</th>
              <th className="pb-2 pr-3">
                <HeaderWithInfo
                  label="Value"
                  meaning="The practical benefit India can get from the agreement (supply continuity, reduced risk, cost/time advantage)."
                  useful="Helps prioritize agreements that deliver measurable outcomes."
                />
              </th>
              <th className="pb-2 pr-3">
                <HeaderWithInfo
                  label="Allied framework"
                  meaning="Whether the agreement also plugs into broader aligned supply-chain frameworks (strategic blocs), not only trade flows."
                  useful="Shows whether the deal can improve India’s geopolitical positioning and resilience."
                />
              </th>
              <th className="pb-2">Cost / gap notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.partner_country}-${i}`} className="border-b border-gray-100 dark:border-gray-800 align-top">
                <td className="py-3 font-medium whitespace-nowrap">
                  {r.partner_flag ? <span className="mr-1">{r.partner_flag}</span> : null}
                  {r.partner_country}
                </td>
                <td className="py-3 text-gray-700 dark:text-gray-300">{r.agreement_short}</td>
                <td className="py-3">
                  <div className="flex flex-col gap-1">
                    <span>{r.stage}</span>
                    {(r.stage_badges || []).map((b) => (
                      <Tag key={b} className="w-fit">{b}</Tag>
                    ))}
                  </div>
                </td>
                <td className="py-3 tabular-nums whitespace-nowrap">{r.trade_value_label}</td>
                <td className="py-3 max-w-[220px]">
                  <p className="font-medium">{r.inside_allied_framework_label}</p>
                  {r.ally_note ? <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{r.ally_note}</p> : null}
                </td>
                <td className="py-3 max-w-sm">
                  <p className="text-sm">{r.cost_of_gap_note}</p>
                  {r.cost_of_gap_highlight ? (
                    <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mt-1">{r.cost_of_gap_highlight}</p>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <div>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Strategic supply chain gaps</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {gaps.map((g, i) => (
            <Card key={`${g.gap_type}-${i}`} className="p-5 flex flex-col h-full">
              <MutedLabel>{g.gap_type}</MutedLabel>
              <div className="flex items-center gap-2 mt-2">
                {g.partner_flag ? <span className="text-lg">{g.partner_flag}</span> : null}
                <h4 className="font-semibold text-gray-900 dark:text-white">{g.partner_short}</h4>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-3 flex-1">{g.description}</p>
              {g.impact_line ? (
                <p className="text-xs font-semibold text-red-700 dark:text-red-300 mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">{g.impact_line}</p>
              ) : null}
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

const DomesticCapabilitySection = () => {
  const route = 'domestic-capability';
  const screen = screenByRoute(route);
  const cards = sample(route, 'pli_program_cards');
  const tableRows = sample(route, 'domestic_cover_comparison_table');

  return (
    <section className="scroll-mt-24 space-y-6">
      <SectionHeader screen={screen} />
      <div className="grid md:grid-cols-2 gap-4">
        {cards.map((c, i) => (
          <Card key={`${c.program}-${i}`} className={`p-5 ${PLI_TONE_BG[c.status_tone] || ''}`}>
            <div className="flex justify-between gap-3 flex-wrap items-start">
              <div>
                <MutedLabel>Production target progress</MutedLabel>
                <h4 className="font-semibold text-lg text-gray-900 dark:text-white mt-1">{c.program}</h4>
              </div>
              <Tag>₹{Number(c.outlay_cr).toLocaleString('en-IN')} Cr outlay</Tag>
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Progress</span>
                <span className="tabular-nums">{c.production_target_pct}%</span>
              </div>
              <div className="h-2.5 bg-white/80 dark:bg-gray-900 rounded-full overflow-hidden border border-gray-200 dark:border-gray-700">
                <div className="h-full bg-gray-900 dark:bg-gray-200 rounded-full" style={{ width: `${Math.min(100, c.production_target_pct)}%` }} />
              </div>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 mt-3">{c.status_summary}</p>
            <div className={`mt-4 rounded-lg border p-3 ${ALLY_TONE[c.ally_qual_tone] || ALLY_TONE.partly}`}>
              <MutedLabel className="!text-[10px]">{c.ally_qual_title}</MutedLabel>
              <p className="text-xs text-gray-800 dark:text-gray-200 mt-1 leading-relaxed">{c.ally_qual_text}</p>
            </div>
          </Card>
        ))}
      </div>
      <Card className="p-5 overflow-x-auto">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Domestic cover vs import dependency</h3>
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-[11px] uppercase text-gray-500 border-b border-gray-200 dark:border-gray-800">
              <th className="pb-2 pr-3">
                <HeaderWithInfo
                  label="Sector"
                  meaning="Strategic sector being assessed for domestic vs import dependency."
                  useful="Lets you compare resilience across sectors that matter to your mandate."
                />
              </th>
              <th className="pb-2 pr-3">
                <HeaderWithInfo
                  label="Demand"
                  meaning="Annual domestic demand or consumption for that sector."
                  useful="Sets the scale of what supply needs to cover."
                />
              </th>
              <th className="pb-2 pr-3">
                <HeaderWithInfo
                  label="Domestic cover"
                  meaning="Share of demand that India can meet from domestic production today."
                  useful="Higher cover means lower disruption risk and stronger self-reliance."
                />
              </th>
              <th className="pb-2 pr-3">
                <HeaderWithInfo
                  label="Import"
                  meaning="Share of demand that is met through imports."
                  useful="Shows how exposed the sector is to external supply shocks."
                />
              </th>
              <th className="pb-2 pr-3">
                <HeaderWithInfo
                  label="Gap target"
                  meaning="Planned domestic cover level by a target year (gap closure goal)."
                  useful="Tracks how aggressively the gap is expected to be closed and by when."
                />
              </th>
              <th className="pb-2 pr-3">
                <HeaderWithInfo
                  label="Allied qualification"
                  meaning="Whether India qualifies as an allied/eligible supplier within key partner frameworks."
                  useful="Indicates access to preferential markets, technology, and supply commitments."
                />
              </th>
              <th className="pb-2">
                <HeaderWithInfo
                  label="Cost of being outside"
                  meaning="What India loses by not being inside the relevant allied framework or capability tier."
                  useful="Quantifies the policy cost of inaction or delay."
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row, i) => (
              <tr key={`${row.sector}-${i}`} className="border-b border-gray-100 dark:border-gray-800 align-top">
                <td className="py-3 font-medium">{row.sector}</td>
                <td className="py-3 tabular-nums">{row.annual_demand_label}</td>
                <td className="py-3">
                  <div className="w-28 h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden mb-1">
                    <div className="h-full bg-emerald-600 dark:bg-emerald-500 rounded-full" style={{ width: `${row.domestic_cover_pct}%` }} />
                  </div>
                  <span className="text-xs tabular-nums">{row.domestic_cover_pct}%</span>
                </td>
                <td className="py-3">{row.import_dependency_label}</td>
                <td className="py-3 text-xs">
                  {row.gap_closure_target ? (
                    <>
                      {row.gap_closure_target.year}: {row.gap_closure_target.target_cover_pct}%
                      <span className="block text-gray-500">cover target</span>
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="py-3 max-w-[200px]">
                  <p className="font-medium text-sm">{row.allied_partner_qual_label}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{row.allied_partner_qual_note}</p>
                </td>
                <td className="py-3 text-xs max-w-[240px]">{row.cost_of_being_outside}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </section>
  );
};

const PortsLogisticsSection = () => {
  const route = 'ports-logistics';
  const screen = screenByRoute(route);
  const corridors = sample(route, 'route_corridor_status');
  const ports = sample(route, 'major_port_performance');
  const abroad = sample(route, 'strategic_port_investments_abroad');
  const bottlenecks = sample(route, 'domestic_logistics_bottlenecks');

  return (
    <section className="scroll-mt-24 space-y-6">
      <SectionHeader screen={screen} />
      <div className="grid lg:grid-cols-3 gap-4">
        {corridors.map((r, i) => (
          <Card key={`${r.name}-${i}`} className="p-5 flex flex-col h-full">
            <div className="flex justify-between items-start gap-2">
              <div>
                <MutedLabel>{r.kind}</MutedLabel>
                <h4 className="font-semibold text-base mt-1 text-gray-900 dark:text-white">{r.name}</h4>
              </div>
              <Tag dot={ROUTE_BAND_DOT[r.risk_band] || ROUTE_BAND_DOT.normal} className="capitalize">{r.risk_band}</Tag>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">{r.description}</p>
            <div className="grid grid-cols-2 gap-3 mt-auto pt-4 border-t border-gray-200 dark:border-gray-800">
              {(r.metrics || []).map((m) => (
                <div key={m.label}>
                  <MutedLabel>{m.label}</MutedLabel>
                  <p className="text-sm font-semibold mt-1">{m.value}</p>
                </div>
              ))}
            </div>
            {(r.sectors_affected || []).length ? (
              <div className="flex flex-wrap gap-1 mt-3">
                {r.sectors_affected.map((s) => (
                  <Tag key={s}>{s}</Tag>
                ))}
              </div>
            ) : null}
          </Card>
        ))}
      </div>
      <Card className="p-5 overflow-x-auto">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Major port performance</h3>
        <table className="w-full text-sm min-w-[800px]">
          <thead>
            <tr className="text-left text-[11px] uppercase text-gray-500 border-b border-gray-200 dark:border-gray-800">
              <th className="pb-2 pr-3">
                <HeaderWithInfo
                  label="Port"
                  meaning="Major Indian port being assessed."
                  useful="Lets you compare performance across the main trade gateways."
                />
              </th>
              <th className="pb-2 pr-3">
                <HeaderWithInfo
                  label="State"
                  meaning="State in which the port is located."
                  useful="Helps coordinate with the relevant state authority for logistics actions."
                />
              </th>
              <th className="pb-2 pr-3">
                <HeaderWithInfo
                  label="Utilisation"
                  meaning="How heavily the port is being used relative to its design capacity."
                  useful="Above-capacity utilisation signals congestion risk and slowdowns."
                />
              </th>
              <th className="pb-2 pr-3">
                <HeaderWithInfo
                  label="Dwell"
                  meaning="Average time cargo sits at the port vs the benchmark."
                  useful="Longer dwell means slower throughput and cost overruns for shippers."
                />
              </th>
              <th className="pb-2 pr-3">
                <HeaderWithInfo
                  label="Status"
                  meaning="Current operational state of the port (normal, elevated, critical)."
                  useful="Quick severity read so you know which ports need attention now."
                />
              </th>
              <th className="pb-2">
                <HeaderWithInfo
                  label="Foreign sovereign exposure"
                  meaning="Extent to which port assets or operations involve foreign-sovereign-linked entities."
                  useful="Surfaces strategic dependence and potential leverage risk at critical gateways."
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {ports.map((p, i) => (
              <tr key={`${p.port}-${i}`} className="border-b border-gray-100 dark:border-gray-800 align-top">
                <td className="py-3 font-medium">{p.port}</td>
                <td className="py-3">{p.state}</td>
                <td className="py-3 tabular-nums">{p.utilisation_pct}%</td>
                <td className="py-3 text-xs tabular-nums">
                  {p.dwell_time_days}d vs {p.benchmark_days}d benchmark
                </td>
                <td className="py-3 capitalize">{p.status}</td>
                <td className="py-3 max-w-md">
                  <span className="font-medium">{p.foreign_sovereign_assessment_label}</span>
                  {p.foreign_sovereign_note ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{p.foreign_sovereign_note}</p>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Strategic port investments abroad</h3>
          <div className="space-y-4">
            {abroad.map((a, i) => (
              <div key={`${a.name}-${i}`} className={`${i ? 'border-t border-gray-100 dark:border-gray-800 pt-4' : ''}`}>
                <Tag>{a.status_label}</Tag>
                <h4 className="font-semibold mt-2 text-gray-900 dark:text-white">{a.name}</h4>
                <p className="text-xs text-gray-500 mt-0.5">{a.location}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{a.situation_summary}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">{a.role}</p>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-5">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Domestic logistics bottlenecks</h3>
          <div className="space-y-4">
            {bottlenecks.map((b, i) => (
              <div
                key={`${b.title}-${i}`}
                className={`rounded-lg border p-3 ${
                  b.severity === 'positive'
                    ? 'border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/40 dark:bg-emerald-950/20'
                    : 'border-amber-200 dark:border-amber-900/50 bg-amber-50/40 dark:bg-amber-950/20'
                }`}
              >
                <p className="font-medium text-sm text-gray-900 dark:text-white">{b.title}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{b.body}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </section>
  );
};

function geoSignalMatchesTab(signal, tabId) {
  if (tabId === 'all') return true;
  const c = (signal.country || '').toLowerCase();
  const src = (signal.source || '').toLowerCase();
  const body = `${signal.title || ''}${signal.body || ''}`.toLowerCase();
  if (tabId === 'china') return c.includes('china') || src.includes('mofcom');
  if (tabId === 'usa') return c.includes('united states') || c.includes('usa') || src.includes('federal register');
  if (tabId === 'russia') return c.includes('russia');
  if (tabId === 'middle_east') return /iran|gcc|opec|middle east|gulf/u.test(body + c);
  return true;
}

const GeopoliticalRiskSection = () => {
  const route = 'geopolitical-risk';
  const screen = screenByRoute(route);
  const tabs = sample(route, 'geopolitical_event_feed_tabs');
  const signals = sample(route, 'geopolitical_signals');
  const leverage = sample(route, 'india_supply_chain_leverage');
  const partners = sample(route, 'strategic_partner_risk_profile');
  const [tabId, setTabId] = useState(tabs.find((t) => t.active)?.id || 'all');

  const visibleSignals = useMemo(() => signals.filter((s) => geoSignalMatchesTab(s, tabId)), [signals, tabId]);

  return (
    <section className="scroll-mt-24">
      <SectionHeader screen={screen} />
      <div className="lg:grid lg:grid-cols-[1fr_320px] gap-6 lg:gap-8">
        <div className="space-y-4 min-w-0">
          <Card className="p-3">
            <div className="flex flex-wrap gap-1">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTabId(t.id)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    tabId === t.id ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </Card>
          {visibleSignals.length === 0 ? (
            <Card className="p-8 text-center text-sm text-gray-500">No signals match this filter.</Card>
          ) : null}
          {visibleSignals.map((s) => (
            <Card key={s.signal_id} className="p-5">
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500 mb-2">
                {s.country_flag ? <span className="text-base">{s.country_flag}</span> : null}
                <span className="font-medium text-gray-800 dark:text-gray-200">{s.country}</span>
                <span>·</span>
                <span>{s.type}</span>
                <span>·</span>
                <span>{s.date_iso}</span>
                <span className="ml-auto inline-flex items-center gap-1">
                  <ExternalLink className="h-3 w-3" aria-hidden /> {s.source}
                </span>
              </div>
              <h4 className="font-medium text-[15px] text-gray-900 dark:text-white">{s.title}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{s.body}</p>
              {s.supply_chain_impact ? (
                <p className="text-sm text-gray-500 mt-3">
                  <span className="text-gray-500 dark:text-gray-400">Supply chain impact · </span>
                  {s.supply_chain_impact}
                </p>
              ) : null}
              {s.intended_outcome ? (
                <div className="mt-4 rounded-lg border border-violet-200 dark:border-violet-900/50 bg-violet-50 dark:bg-violet-950/25 px-3 py-2.5">
                  <MutedLabel className="!text-violet-800 dark:!text-violet-200 normal-case tracking-normal text-xs font-semibold">
                    Intended outcome
                  </MutedLabel>
                  <p className="text-sm text-violet-900 dark:text-violet-100 mt-1 leading-relaxed">{s.intended_outcome}</p>
                </div>
              ) : null}
              {s.tags?.length ? (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {s.tags.map((tg, idx) => (
                    <span
                      key={`${tg.label}-${idx}`}
                      className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium border ${GP_TAG_KIND[tg.kind] || GP_TAG_KIND.neutral}`}
                    >
                      {tg.label}
                    </span>
                  ))}
                </div>
              ) : null}
            </Card>
          ))}
        </div>
        <aside className="space-y-4 mt-8 lg:mt-0 lg:sticky lg:top-24 self-start">
          <Card className="p-5">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">India supply chain leverage</h3>
            <div className="space-y-4">
              {leverage.map((item, i) => (
                <div key={`${item.title}-${i}`} className={i ? 'pt-4 border-t border-gray-100 dark:border-gray-800' : ''}>
                  <Tag className="mb-2">{item.tag}</Tag>
                  <h4 className="font-medium text-sm text-gray-900 dark:text-white">{item.title}</h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{item.description}</p>
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-5 overflow-x-auto">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm">Strategic partner risk profile</h3>
            <table className="w-full text-xs min-w-[280px]">
              <thead>
                <tr className="text-left text-[10px] uppercase text-gray-500 border-b border-gray-200 dark:border-gray-800">
                  <th className="pb-2 pr-2">Partner</th>
                  <th className="pb-2 pr-2">Relationship</th>
                  <th className="pb-2 pr-2">Key dependency</th>
                  <th className="pb-2">India leverage</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((pr, i) => (
                  <tr key={`${pr.partner}-${i}`} className="border-b border-gray-100 dark:border-gray-800 align-top">
                    <td className="py-2 pr-2 font-medium whitespace-nowrap">
                      {pr.partner_flag ? <span className="mr-0.5">{pr.partner_flag}</span> : null}
                      {pr.partner}
                    </td>
                    <td className="py-2 pr-2">{pr.relationship}</td>
                    <td className="py-2 pr-2">{pr.key_dependency}</td>
                    <td className="py-2 text-gray-600 dark:text-gray-400">{pr.india_leverage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </aside>
      </div>
    </section>
  );
};

const URGENCY_STYLE = {
  immediate: 'border-red-200 dark:border-red-900/50',
  month: 'border-amber-200 dark:border-amber-900/50',
  quarter: 'border-gray-200 dark:border-gray-800',
};

const ScenariosSection = () => {
  const route = 'scenarios';
  const screen = screenByRoute(route);
  const scenarioButtons = sample(route, 'scenario_buttons');
  const contentById = sampleObject(route, 'scenario_content') || {};
  const defaultId = scenarioButtons.find((b) => b.default_active)?.scenario_id || scenarioButtons[0]?.scenario_id;
  const [activeId, setActiveId] = useState(defaultId);

  const active = contentById[activeId];

  return (
    <section className="scroll-mt-24 space-y-6">
      <SectionHeader screen={screen} />
      <Card className="p-4">
        <MutedLabel className="mb-2">Choose a disruption scenario</MutedLabel>
        <div className="flex flex-wrap gap-2">
          {scenarioButtons.map((opt) => (
            <button
              key={opt.scenario_id}
              type="button"
              onClick={() => setActiveId(opt.scenario_id)}
              className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                activeId === opt.scenario_id
                  ? 'bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </Card>
      {!active ? (
        <Card className="p-5"><p className="text-sm text-gray-500">No scenario content for selection.</p></Card>
      ) : (
        <>
          {active.trigger_political_question?.text ? (
            <Card className="p-5 border-indigo-200 dark:border-indigo-900/50 bg-indigo-50/50 dark:bg-indigo-950/20">
              <MutedLabel>{active.trigger_political_question.label}</MutedLabel>
              <p className="text-sm text-gray-800 dark:text-gray-200 mt-2 leading-relaxed">{active.trigger_political_question.text}</p>
            </Card>
          ) : null}
          {active.scenario_kpis?.length ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {active.scenario_kpis.map((k, i) => (
                <Card key={`${k.label}-${i}`} className="p-4">
                  <MutedLabel>{k.label}</MutedLabel>
                  <p className="text-2xl font-semibold mt-2 tabular-nums text-gray-900 dark:text-white">{k.value}</p>
                  {k.sub ? <p className="text-xs text-gray-500 mt-1">{k.sub}</p> : null}
                </Card>
              ))}
            </div>
          ) : null}
          {active.projection?.text ? (
            <Card className="p-5">
              <MutedLabel>{active.projection.label}</MutedLabel>
              <p className="text-sm text-gray-700 dark:text-gray-300 mt-3 leading-relaxed">{active.projection.text}</p>
              {active.projection.alternative_sources?.length ? (
                <>
                  <MutedLabel className="mt-4">{active.projection.alternative_sources_heading}</MutedLabel>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {active.projection.alternative_sources.map((src) => (
                      <Tag key={String(src)}>{String(src)}</Tag>
                    ))}
                  </div>
                </>
              ) : null}
            </Card>
          ) : null}
          {active.strategic_import_baseline?.length ? (
            <Card className="p-5 overflow-x-auto">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Strategic import baseline</h3>
              <table className="w-full text-sm min-w-[700px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase text-gray-500 border-b border-gray-200 dark:border-gray-800">
                    <th className="pb-2 pr-3">Category</th>
                    <th className="pb-2 pr-3">Primary source</th>
                    <th className="pb-2 pr-3">Share</th>
                    <th className="pb-2 pr-3">Annual import</th>
                    <th className="pb-2 pr-3">Buffer</th>
                    <th className="pb-2">Alternatives</th>
                  </tr>
                </thead>
                <tbody>
                  {active.strategic_import_baseline.map((row, i) => (
                    <tr key={`${row.category}-${i}`} className="border-b border-gray-100 dark:border-gray-800 align-top">
                      <td className="py-2.5 font-medium">{row.category}</td>
                      <td className="py-2.5">
                        {row.primary_source_flag ? <span className="mr-1">{row.primary_source_flag}</span> : null}
                        {row.primary_source}
                      </td>
                      <td className="py-2.5 tabular-nums">{row.source_share_pct}%</td>
                      <td className="py-2.5">{row.annual_import_label}</td>
                      <td className="py-2.5">{row.industry_buffer}</td>
                      <td className="py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {(row.alternatives || []).map((alt) => (
                            <Tag key={alt}>{alt}</Tag>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ) : null}
          {active.recommended_interventions?.length ? (
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Recommended interventions</h3>
              <div className="space-y-3">
                {active.recommended_interventions.map((iv, i) => (
                  <Card key={`${iv.title}-${i}`} className={`p-5 border-l-4 ${URGENCY_STYLE[iv.urgency] || URGENCY_STYLE.quarter}`}>
                    <div className="flex flex-wrap gap-2 items-center text-[11px] text-gray-500 mb-2">
                      <Tag>{iv.urgency_label}</Tag>
                      <Tag>{iv.type_label}</Tag>
                      <span className="tabular-nums">Cost: {iv.cost_to_govt}</span>
                      <span>·</span>
                      <span>{iv.time_to_impact}</span>
                    </div>
                    <h4 className="font-medium text-gray-900 dark:text-white">{iv.title}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{iv.description}</p>
                  </Card>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};

export function PolicyMakerUseCaseScreenContent({ routeId, profile }) {
  const showCommandChrome = routeId === 'alerts';

  const body = () => {
    switch (routeId) {
      case 'alerts':
        return <AlertsSection />;
      case 'import-exposure':
        return <ImportExposureSection />;
      case 'trade-agreements':
        return <TradeAgreementsSection />;
      case 'domestic-capability':
        return <DomesticCapabilitySection />;
      case 'ports-logistics':
        return <PortsLogisticsSection />;
      case 'geopolitical-risk':
        return <GeopoliticalRiskSection />;
      case 'scenarios':
        return <ScenariosSection />;
      default:
        return <AlertsSection />;
    }
  };

  return (
    <div className="space-y-8">
      {showCommandChrome ? <Hero profile={profile} /> : null}
      {showCommandChrome ? <KpiStrip /> : null}
      {body()}
    </div>
  );
}
